export type Block = {
    cells: {
        date: Date;
        label: string;
    }[];
    start: Date;
    end: Date;
    label: string;
};
const getWeekDayNumber = (date: Date) => {
    const dayStr = date.toLocaleString('en', { weekday: 'short' });
    if (dayStr === 'Mon') return 0;
    if (dayStr === 'Tue') return 1;
    if (dayStr === 'Wed') return 2;
    if (dayStr === 'Thu') return 3;
    if (dayStr === 'Fri') return 4;
    if (dayStr === 'Sat') return 5;
    if (dayStr === 'Sun') return 6;
    throw new Error(`Unknown day of week: ${dayStr}`);
};

export const zoomRanges: ZoomRangePreset[] = [
    {
        // day
        scrollLevelMin: 1920,
        scrollLevelMax: 3840,
        getCellLabel: (date: Date) => `${date.toLocaleString('en', { day: '2-digit' })} ${date.toLocaleString('en', { weekday: 'short' })}`,
        getBlockLabel: (date: Date) =>
            date.toLocaleString('en', {
                month: 'short',
                year: 'numeric',
            }),
        getBlockStart: (date: Date) => {
            const weekDay = getWeekDayNumber(date);
            date.setDate(date.getDate() - weekDay);
            date.setHours(0);
            date.setMinutes(0);
            date.setSeconds(0);
            date.setMilliseconds(0);
        },
        getBlockEnd: (date: Date) => {
            date.setHours(0);
            date.setMinutes(0);
            date.setSeconds(0);
            date.setMilliseconds(0);
            const weekDay = getWeekDayNumber(date);
            date.setDate(date.getDate() + 7 - weekDay);
        },
        cellPxMin: 50,
        cellPxMax: 800,
        cellMs: 1000 * 60 * 60 * 24,
    },
    {
        // hour
        scrollLevelMin: 960,
        scrollLevelMax: 1920,
        getCellLabel: (date: Date) => date.toLocaleString('en', { hour: '2-digit', hour12: false }),
        getBlockLabel: (date: Date) =>
            date.toLocaleString('en', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            }),
        getBlockStart: (date: Date) => {
            date.setHours(0);
            date.setMinutes(0);
            date.setSeconds(0);
            date.setMilliseconds(0);
        },
        getBlockEnd: (date: Date) => {
            // Use date arithmetic instead of hour arithmetic to handle DST properly
            date.setDate(date.getDate() + 1);
            return date.getTime();
        },
        cellPxMin: 50,
        cellPxMax: 300,
        cellMs: 1000 * 60 * 60,
    },
    {
        // quarter-hour
        scrollLevelMin: 480,
        scrollLevelMax: 960,
        getCellLabel: (date: Date) => date.toLocaleString('en', { minute: '2-digit' }),
        getBlockLabel: (date: Date) =>
            date.toLocaleString('en', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
            }),
        getBlockStart: (date: Date) => {
            date.setMinutes(0);
            date.setSeconds(0);
            date.setMilliseconds(0);
        },
        getBlockEnd: (date: Date) => date.setHours(date.getHours() + 1),
        cellPxMin: 50,
        cellPxMax: 300,
        cellMs: 1000 * 60 * 15,
    },
    {
        // minute
        scrollLevelMin: 0,
        scrollLevelMax: 480,
        getCellLabel: (date: Date) => date.toLocaleString('en', { minute: '2-digit' }),
        getBlockLabel: (date: Date) =>
            date.toLocaleString('en', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
            }),
        getBlockStart: (date: Date) => {
            date.setMinutes(0);
            date.setSeconds(0);
            date.setMilliseconds(0);
        },
        getBlockEnd: (date: Date) => date.setHours(date.getHours() + 1),
        cellPxMin: 20,
        cellPxMax: 80,
        cellMs: 1000 * 60,
    },
];
export type ZoomRangePreset = {
    scrollLevelMin: number;
    scrollLevelMax: number;
    getCellLabel: (date: Date) => string;
    getBlockLabel: (date: Date) => string;
    getBlockStart: (date: Date) => void;
    getBlockEnd: (date: Date) => void;
    cellPxMin: number;
    cellPxMax: number;
    cellMs: number;
};
export type ZoomRange = {
    scrollLevel: number;
    zoomRangePreset: ZoomRangePreset;
    cellPx: number;
};
export type ZoomRangeWithBlocks = ZoomRange & {
    blocks: Block[];
};

export const getZoom: (scrollLevel: number, zoomRanges: ZoomRangePreset[]) => ZoomRange | null = (scrollLevel, zoomRanges) => {
    if (zoomRanges.length === 0) return null;

    let zoomRangeI = -1;
    if (scrollLevel === zoomRanges[0].scrollLevelMax) {
        zoomRangeI = 0;
    } else {
        zoomRangeI = zoomRanges.findIndex((zoom) => zoom.scrollLevelMin <= scrollLevel && zoom.scrollLevelMax > scrollLevel);
    }
    if (zoomRangeI === -1) {
        return null;
    }

    const zoomRange = zoomRanges[zoomRangeI];
    if (zoomRange.scrollLevelMin === scrollLevel)
        return {
            scrollLevel,
            cellPx: zoomRange.cellPxMax,
            zoomRangePreset: zoomRange,
        };

    if (zoomRange === undefined) return null;

    // not an exact value, return lerped
    const lerp = (zoomRange.scrollLevelMax - scrollLevel) / (zoomRange.scrollLevelMax - zoomRange.scrollLevelMin);
    const toReturn: ZoomRange = {
        scrollLevel,
        cellPx: lerp * (zoomRange.cellPxMax - zoomRange.cellPxMin) + zoomRange.cellPxMin,
        zoomRangePreset: zoomRange,
    };
    return toReturn;
};

export const getBlocks = (zoomRange: ZoomRange, displayDate: Date, totalPxToDisplay: number) => {
    const pxPerMs = zoomRange.cellPx / zoomRange.zoomRangePreset.cellMs;
    const displayDateFuture = new Date(displayDate);

    zoomRange.zoomRangePreset.getBlockStart(displayDateFuture);

    const centerDateBlock = new Date(displayDateFuture);
    let blocksCount = 0;
    const futureBlockDates: { start: Date; end: Date }[] = [];
    const totalMsToDisplay = totalPxToDisplay / pxPerMs;
    const maxMs = displayDate.getTime() + totalMsToDisplay / 2;

    while (displayDateFuture.getTime() <= maxMs) {
        const start = new Date(displayDateFuture);
        zoomRange.zoomRangePreset.getBlockEnd(displayDateFuture);
        futureBlockDates.push({ start, end: new Date(displayDateFuture) });
        blocksCount++;
    }

    const displayDatePast = new Date(centerDateBlock);
    displayDatePast.setMilliseconds(displayDatePast.getMilliseconds() - 1);
    const pastBlockDates: { start: Date; end: Date }[] = [];
    const minMs = displayDate.getTime() - totalPxToDisplay / (pxPerMs * 2);

    while (displayDatePast.getTime() >= minMs) {
        const end = new Date(displayDatePast);
        displayDatePast.setMilliseconds(displayDatePast.getMilliseconds() - 1);
        zoomRange.zoomRangePreset.getBlockStart(displayDatePast);
        pastBlockDates.push({ end, start: new Date(displayDatePast) });
    }

    pastBlockDates.reverse();

    const blocks = [...pastBlockDates, ...futureBlockDates];

    return blocks.map((block) => {
        const cells = getCellsInBlock(block, zoomRange).map((cell) => ({
            date: cell,
            label: zoomRange.zoomRangePreset.getCellLabel(cell),
        }));
        return {
            ...block,
            label: zoomRange.zoomRangePreset.getBlockLabel(block.start),
            cells,
        };
    });
};

const getCellsInBlock = ({ start, end }: { start: Date; end: Date }, zoom: ZoomRange) => {
    const cells: Date[] = [];
    const current = new Date(start);

    // For day-level cells, use date arithmetic to avoid DST issues
    if (zoom.zoomRangePreset.cellMs === 1000 * 60 * 60 * 24) {
        while (current.getTime() < end.getTime()) {
            cells.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }
    } else {
        // For hour/minute level cells, millisecond arithmetic is fine
        const msDiff = end.getTime() - start.getTime();
        const cellCount = msDiff / zoom.zoomRangePreset.cellMs;
        for (let i = 0; i < cellCount; i++) {
            cells.push(new Date(start.getTime() + i * zoom.zoomRangePreset.cellMs));
        }
    }

    return cells;
};

export const getZoomWithBlocks: (
    scrollLevel: number,
    zoomRanges: ZoomRangePreset[],
    displayDate: Date,
    totalPxToDisplay: number,
) => ZoomRangeWithBlocks | null = (scrollLevel, zoomRanges, displayDate, totalPxToDisplay) => {
    const zoom = getZoom(scrollLevel, zoomRanges);
    if (zoom === null) return null;
    const blocks = getBlocks(zoom, displayDate, totalPxToDisplay);
    return {
        ...zoom,
        blocks,
    };
};
